import { ethers } from "ethers";
import { BASE_SEPOLIA_TOKENS, EVM_CONFIG, type TokenInfo } from "../../config";
import { NoteTokenType } from "../../joinsplit/types";

export type ScreeningStatus =
  | "idle"
  | "checking"
  | "passed"
  | "blocked"
  | "error";

export function parseMpk(input: string): bigint | null {
  try {
    if (!input) return null;
    const value = input.trim();
    if (!value.startsWith("0x")) return null;
    return BigInt(value);
  } catch {
    return null;
  }
}

export function maskHex(value: string, left = 8, right = 6): string {
  const normalized = String(value || "").trim();
  if (normalized.length <= left + right + 3) return normalized;
  return `${normalized.slice(0, left)}...${normalized.slice(-right)}`;
}

export function buildEngineUrl(basePath: string, endpoint: string): string {
  if (basePath.includes("?path=")) {
    return `${basePath}${endpoint.replace(/^\//, "")}`;
  }
  return `${basePath.replace(/\/+$/, "")}${endpoint}`;
}

export function isLikelySolanaPubkey(value: string): boolean {
  const raw = String(value || "").trim();
  if (!raw || raw.length < 32 || raw.length > 44) return false;
  if (raw.startsWith("0x")) return false;
  return /^[1-9A-HJ-NP-Za-km-z]+$/.test(raw);
}

export function tokenTypeToNoteTokenType(
  tokenType: TokenInfo["tokenType"],
): NoteTokenType {
  if (tokenType === "erc721") return NoteTokenType.ERC721;
  if (tokenType === "erc1155") return NoteTokenType.ERC1155;
  if (tokenType === "vnft") return NoteTokenType.VNFT;
  return NoteTokenType.ERC20;
}

export function buildRuntimeToken(
  token: TokenInfo,
  tokenSubIDOverride?: string,
): TokenInfo {
  if (
    token.tokenType === "erc721" ||
    token.tokenType === "erc1155" ||
    token.tokenType === "vnft"
  ) {
    return {
      ...token,
      tokenSubID: (tokenSubIDOverride ?? token.tokenSubID ?? "").trim(),
    };
  }
  return token;
}

export function applyWrappedNftMode(
  token: TokenInfo,
  wrappedMode: boolean,
): TokenInfo {
  if (token.tokenType !== "erc721" && token.tokenType !== "erc1155")
    return token;
  return {
    ...token,
    isWrappedNft: wrappedMode,
  };
}

export function tokenOptionValue(token: TokenInfo): string {
  const tokenType = token.tokenType ?? "erc20";
  const base = token.address.toLowerCase();
  const wrappedPrefix = token.isWrappedNft ? "wrapped:" : "";
  if (
    tokenType === "erc721" ||
    tokenType === "erc1155" ||
    tokenType === "vnft"
  ) {
    return `${wrappedPrefix}${base}:${tokenType}:${(token.tokenSubID || "0").trim()}`;
  }
  return `${wrappedPrefix}${base}`;
}

export function getNftDisplayTokenId(token: TokenInfo): string {
  if (token.isWrappedNft) {
    const wrappedId = (token.wrappedUnderlyingTokenSubID ?? "").trim();
    if (wrappedId) return wrappedId;
  }
  return (token.tokenSubID ?? "0").trim() || "0";
}

export function getNftDisplayLabel(token: TokenInfo): string {
  const tokenId = getNftDisplayTokenId(token);
  const baseName =
    (token.wrappedName || token.name || "").trim() ||
    (token.tokenType === "erc1155"
      ? "ERC1155 NFT"
      : token.tokenType === "vnft"
        ? "vStealth"
        : "ERC721 NFT");
  if (!tokenId) return baseName;
  return baseName.includes(`#${tokenId}`)
    ? baseName
    : `${baseName} #${tokenId}`;
}

export const PAYMENT_LINK_TOKEN_OPTIONS: TokenInfo[] = [
  ...BASE_SEPOLIA_TOKENS,
  {
    address: "nft:any:erc721",
    symbol: "NFT",
    name: "Any ERC-721 NFT",
    decimals: 0,
    isNative: false,
    tokenType: "erc721",
  },
  {
    address: "nft:any:erc1155",
    symbol: "NFT",
    name: "Any ERC-1155 NFT",
    decimals: 0,
    isNative: false,
    tokenType: "erc1155",
  },
];

export const DEFAULT_BRIDGE_TOKEN =
  BASE_SEPOLIA_TOKENS.find((token) => !token.isNative) ||
  BASE_SEPOLIA_TOKENS[0];

export const BRIDGE_DESTINATION_CHAIN = "solana";
export const BRIDGE_DESTINATION_CHAIN_ID = 7565164;

export const SOLANA_BRIDGE_MINT_BY_BASE_TOKEN: Record<string, string> = {
  "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913":
    "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
  "0xfde4c96c8593536e31f229ea8f37b2ada2699bb2":
    "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
};

export const DEFAULT_PERPS_ASSET_TOKEN_ADDRESS = (
  process.env.REACT_APP_BASE_PERPS_ASSET_TOKEN_ADDRESS ||
  process.env.REACT_APP_BASE_PERPS_USDC_TOKEN_ADDRESS ||
  "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"
).trim();

export const DEFAULT_PERPS_ASSET_TOKEN_SYMBOL =
  (process.env.REACT_APP_BASE_PERPS_ASSET_TOKEN_SYMBOL || "USDC")
    .trim()
    .toUpperCase() || "USDC";

export const DEFAULT_PERPS_ASSET_TOKEN_NAME =
  (process.env.REACT_APP_BASE_PERPS_ASSET_TOKEN_NAME || "USD Coin").trim() ||
  "USD Coin";

export const DEFAULT_PERPS_ASSET_TOKEN_DECIMALS =
  Number(process.env.REACT_APP_BASE_PERPS_ASSET_TOKEN_DECIMALS || "6") || 6;

export function parseTokenAmountBase(
  amount: string,
  decimals: number,
): bigint | null {
  try {
    return ethers.parseUnits(amount || "0", decimals);
  } catch {
    return null;
  }
}
